import { reactive } from '@vue/reactivity'
import { ActivePage } from '../enums/ActivePage'
import { BulkVisitorAnswerMetadataKey } from '../enums/BulkVisitorAnswerMetadataKey'

const state = reactive({
    activePage: ActivePage.Landing,
    webFormOpenedInNewTab: false,
})

export class Navigation {

    /**
     * TODO: Refactor to get web form requests happening properly and being properly associated with the landing for ref
     * Also need to update the router to handle the new web form routes
     */

    static WEB_FORM_TAB = 'web-form-tab'

    init(formPath, bypassLoadingPage, hasInlineWebForm, isInlineWithRedirect, hasGroups, frontendRoutePath, startWebFormRedirectUrl, startWebFormInNewTab, startWebFormInNewTabUrl, initialActivePage = null, webFormSlug = null) {

        // Set the correct initial active page from the start
        if (initialActivePage) {
            this.activePage = mapInitialActivePageToActivePage(initialActivePage)
        }

        initRouter(frontendRoutePath, hasInlineWebForm)
        this.frontendRoutePath = frontendRoutePath
        this.webFormSlug = webFormSlug ?? window.__STATE?.webForm?.slug ?? null
        this.formPath = formPath || ''
        this.bypassLoadingPage = bypassLoadingPage || false
        this.hasInlineWebForm = hasInlineWebForm ?? false
        this.isInlineWithRedirect = isInlineWithRedirect ?? false
        this.hasGroups = hasGroups ?? true
        this.step = this.hasInlineWebForm ? 1 : 0
        this.startWebFormRedirectUrl = startWebFormRedirectUrl ?? null
        this.startWebFormInNewTab = startWebFormInNewTab ?? false
        this.startWebFormInNewTabUrl = startWebFormInNewTabUrl ?? null
        Arbor.watch(() => Arbor.router().path, (newPath, oldPath) => handleRoute(newPath, oldPath, this.formPath))
        window.addEventListener('popstate', () => handleStep())

    }

    get activePage() {
        return state.activePage
    }

    set activePage(value) {
        state.activePage = value
    }

    get webFormOpenedInNewTab() {
        return state.webFormOpenedInNewTab
    }

    set webFormOpenedInNewTab(value) {
        state.webFormOpenedInNewTab = value
    }

    get shouldInlineWithRedirect() {
        return this.isInlineWithRedirect && window.__WINDOW_NAME !== Navigation.WEB_FORM_TAB
    }

    start() {

        if (!this.shouldInlineWithRedirect && this.startWebFormInNewTab && this.startWebFormInNewTabUrl && window.__WINDOW_NAME !== Navigation.WEB_FORM_TAB) {

            this.openNewTab(this.startWebFormInNewTabUrl)

            return true

        }

        if (this.startWebFormRedirectUrl && window.__WINDOW_NAME !== Navigation.WEB_FORM_TAB) {

            window.location.href = this.startWebFormRedirectUrl

            return true

        }

        /*
         * Checking for step 0 to avoid setting the step=1 on initial load, as this will trigger a navigation to the webform view
         * Step will be set to one, once the webform controller.start() is called
         */
        if (this.hasInlineWebForm && Arbor.store('qv').webForm.state.step !== 0) {
            Arbor.store('qv').webForm.state.step = 1
        }

        Arbor.store('qv').webForm.controller.start()

        return true

    }

    goTo(path, replace) {

        const method = replace ? 'replace' : 'push'

        Arbor.$router[ method ](path)

        if (!this.hasInlineWebForm) {
            scrollToTop()
        }

    }

    goToStep(step, replace) {
        this.goTo(buildFullPath(this.frontendRoutePath, step), replace)
    }

    goToLoadingSteps() {
        this.goTo(buildFullPath(this.frontendRoutePath, 'loading'))
    }

    goToThankYouPage() {
        this.goTo(buildFullPath(this.frontendRoutePath, 'thank-you'))
    }

    scrollToTop() {
        return scrollToTop()
    }

    /**
     * @param {string} uri
     */
    redirectTo(uri) {
        window.location.href = uri
    }

    openNewTab(url) {

        newWindow(url, Navigation.WEB_FORM_TAB)
        this.webFormOpenedInNewTab = true

    }

    openNewTabWithQueryString(data) {

        const url = prepareUrl(this.startWebFormInNewTabUrl, data)

        this.openNewTab(url)

    }

}

// Expose constant globally for use in Blade templates
if (typeof window !== 'undefined') {
    window.__NAVIGATION_WEB_FORM_TAB = Navigation.WEB_FORM_TAB
}

function handleRoute(newPath, oldPath, formPath) {

    /**
     * If the user reaches the loading or thank-you page and tries to go back
     * we'll redirect them to the start of the web-form.
     */
    if (oldPath.endsWith('/thank-you') || (oldPath.endsWith('/loading') && !newPath.endsWith('/thank-you'))) {
        window.location = formPath
    }

}

function handleStep() {

    Arbor.nextTick(async() => {

        const currentStep = parseInt(Arbor.$router.params.step)
        const cachedStep = Arbor.store('qv').webForm.controller.cachedSteps[ currentStep ]

        Arbor.store('qv').webForm.state.step = currentStep

        if (cachedStep) {

            Arbor.store('qv').webForm.state.webFormContent = cachedStep
            Arbor.store('qv').webForm.state.canSubmit = true

        }

        if (
            Arbor.store('qv').webForm.state.usesFlexibleJourney
            && !Number.isNaN(currentStep)
            && currentStep > 0
        ) {
            await Arbor.store('qv').webForm.controller.syncFlexiblePageIndex(currentStep - 1)
        }

    })

}

function initRouter(frontendRoutePath, hasInlineWebForm) {

    const $router = Arbor.router()

    $router.addRoutes([
        '/landing-page-preview/:slug/?',
        `${ frontendRoutePath }/:slug/?`,
        `${ frontendRoutePath }/:slug/loading/?`,
        `${ frontendRoutePath }/:slug/thank-you/?`,
        `${ frontendRoutePath }/:slug/step/:step(\\d+)?/?`,
        `${ frontendRoutePath }/:slug/:step(\\d+)?/?`,
    ])

    $router.afterEach(() => {

        /**
         * Smooth scrolling can get cancelled if the content length has a large change.
         * This is a workaround to ensure the page is scrolled to the top if the
         * scroll was silently cancelled.
         *
         * This is duplicated from how our Vue implementation works.
         */
        if (hasInlineWebForm && Arbor.store('qv').navigation.activePage === ActivePage.Landing) {
            return
        }

        scrollToTop()

    })

    openModalByRoute($router)

}

function openModalByRoute($router) {

    setTimeout(() => {

        const modalId = $router.hash.replace(new RegExp('^#'), '')

        if (!modalId) {
            return
        }

        showDynamicModalById(modalId)

    }, 400)

}

function showDynamicModalById(modalId) {

    if (modalId === 'contact') {
        return Arbor.store('qv').modal.openContactUsModal()
    }

    Arbor.store('qv').modal.openModal(modalId)

}

function scrollToTop() {
    window.scrollTo({ top: -1, left: 0, behavior: 'smooth' })
}

function buildFullPath(frontendRoutePath, path = null) {

    let fullPath

    const slug = Arbor.$router.params.slug ?? Arbor.store('qv').navigation.webFormSlug

    switch (path) {

    case 'loading':
        fullPath = `${ frontendRoutePath }/${ slug }/loading`

        break

    case 'thank-you':
        fullPath = `${ frontendRoutePath }/${ slug }/thank-you`

        break

    default:
        fullPath = `${ frontendRoutePath }/${ slug }/${ path }`

    }

    if (window.location.search?.length) {
        fullPath += window.location.search
    }

    return fullPath

}

function mapInitialActivePageToActivePage(initialActivePage) {

    switch (initialActivePage) {

    case 'web-form-chat':
        return ActivePage.WebFormChat

    default:
        return ActivePage.Landing

    }

}

/**
 * @param {string} uri
 * @param {string} target
 */
function newWindow(uri, target = '_blank') {
    window.open(uri, target)
}

function prepareUrl(startWebFormInNewTabUrl, data) {

    const url = new URL(startWebFormInNewTabUrl)
    const parameters = new URLSearchParams(data)

    const excludedKeys = [
        'offset',
        ...Object.values(BulkVisitorAnswerMetadataKey),
    ]

    for (const [ key, value ] of parameters.entries()) {

        if (excludedKeys.includes(key)) {
            continue
        }

        url.searchParams.set(key, value)

    }

    return url.toString()

}
